import React, { useState, useRef } from "react";
import { Modal, Form, Input, InputNumber, DatePicker, Select, Alert, Tour, Space, Button, message } from "antd";
import { QuestionCircleOutlined } from "@ant-design/icons";
import { useCreate } from "../../../../hooks";
import { mawbUsageService } from "../../../../api/enhancedApi";

const { Option } = Select;

/**
 * 主单号分配弹窗
 *
 * 两种模式（由 usageIds 决定）：
 *  - 传入 usageIds（数组，列表勾选 / 批量 / 行内分配）：分配选中的具体主单号，数量固定
 *  - 不传 usageIds（详情页右上角「分配号码」）：按数量分配，自动取前 N 个未分配明细
 */
const MawbAllocationModal = ({ open, onCancel, onSuccess, stock, usageIds, autoGuide = false }) => {
    const [form] = Form.useForm();
    const [guideOpen, setGuideOpen] = useState(false);

    // 弹窗内字段引导锚点
    const nameRef = useRef(null);
    const typeRef = useRef(null);
    const qtyRef = useRef(null);
    const startRef = useRef(null);
    const dateRef = useRef(null);

    const rangeStart = stock?.start_serial != null ? parseInt(stock.start_serial, 10) : undefined;
    const rangeEnd = stock?.end_serial != null ? parseInt(stock.end_serial, 10) : undefined;

    const byIds = Array.isArray(usageIds) && usageIds.length > 0;
    const availableQuantity = stock?.available_quantity ?? 0;
    const canAllocate = byIds ? true : availableQuantity > 0;

    const createMutation = useCreate("分配主单号", mawbUsageService.allocate, {
        onSuccess: (res) => {
            message.success(res?.data?.message || "分配成功");
            form.resetFields();
            onSuccess();
        },
    });

    const handleOk = async () => {
        if (!byIds && !canAllocate) {
            message.warning("该号段已无可用主单号，无法分配");
            return;
        }
        try {
            const values = await form.validateFields();
            const payload = {
                stock_id: stock.id,
                allocated_to_name: values.allocated_to_name,
                allocated_to_type: values.allocated_to_type || "partner",
                allocation_date: values.allocation_date.format("YYYY-MM-DD"),
            };
            if (byIds) {
                payload.usage_ids = usageIds;
            } else {
                payload.quantity = values.quantity;
                if (values.start_serial != null && values.start_serial !== "") {
                    payload.start_serial = values.start_serial;
                }
            }
            createMutation.mutate(payload);
        } catch {
            // validation failed
        }
    };

    // 用 Modal 动画结束的原生时机启动引导：字段已渲染且位置稳定
    const handleAfterOpenChange = (opened) => {
        if (opened) {
            if (autoGuide) setGuideOpen(true);
        } else {
            setGuideOpen(false);
        }
    };

    const guideSteps = [
        {
            title: "分配对象",
            description: "填写这批主单号要分配给谁，自由文本，如「顺丰」「XX 货代」或内部部门名称。",
            target: () => nameRef.current,
        },
        {
            title: "分配类型",
            description: "区分对象是「合作伙伴」还是「内部使用」，方便后续统计与对账。",
            target: () => typeRef.current,
        },
        ...(byIds
            ? []
            : [
                  {
                      title: "分配数量",
                      description: `按数量分配时填这里，系统取 N 个未分配主单号，最多 ${availableQuantity} 个。`,
                      target: () => qtyRef.current,
                  },
                  {
                      title: "起始号（选填）",
                      description: "想从指定号开始发？填这里，系统会从「不小于该序列号」的可用号里向后取 N 个；留空则从最小可用号开始。",
                      target: () => startRef.current,
                  },
              ]),
        {
            title: "分配日期",
            description: "记录本次分配发生的日期。",
            target: () => dateRef.current,
        },
        {
            title: "确认分配",
            description: byIds
                ? "确认无误后点「确定」，选中的主单号会标记为「已分配」。"
                : "确认无误后点「确定」，系统会自动取出相应数量的主单号并标记为「已分配」。",
        },
    ];

    return (
        <Modal
            title={
                <Space>
                    {`分配主单号 — ${stock?.airline_prefix || ""} 号段`}
                    <Button
                        type="text"
                        size="small"
                        icon={<QuestionCircleOutlined />}
                        onClick={() => setGuideOpen(true)}
                    >
                        填写引导
                    </Button>
                </Space>
            }
            open={open}
            onOk={handleOk}
            onCancel={onCancel}
            confirmLoading={createMutation.isLoading}
            okButtonProps={{ disabled: !canAllocate }}
            afterOpenChange={handleAfterOpenChange}
            destroyOnClose
            width={480}
        >
            {stock && (
                <div style={{ marginBottom: 16, color: "#666", fontSize: 13 }}>
                    号段：{stock.start_serial}~{stock.end_serial}，可用：{availableQuantity} 个
                </div>
            )}
            {byIds && (
                <Alert
                    style={{ marginBottom: 16 }}
                    type="info"
                    showIcon
                    message={`已选中 ${usageIds.length} 个主单号，将分配给同一对象`}
                />
            )}
            <Form form={form} layout="vertical" preserve={false}>
                <div ref={nameRef}>
                    <Form.Item
                        name="allocated_to_name"
                        label="分配对象"
                        rules={[
                            { required: true, message: "请输入分配对象" },
                            { whitespace: true, message: "分配对象不能为空" },
                        ]}
                    >
                        <Input maxLength={200} placeholder="如：顺丰、XX货代、内部XX部门" />
                    </Form.Item>
                </div>
                <div ref={typeRef}>
                    <Form.Item name="allocated_to_type" label="分配类型" initialValue="partner">
                        <Select>
                            <Option value="partner">合作伙伴</Option>
                            <Option value="internal">内部使用</Option>
                        </Select>
                    </Form.Item>
                </div>
                {!byIds && (
                    <div ref={qtyRef}>
                        <Form.Item
                            name="quantity"
                            label="分配数量"
                            rules={[
                                { required: true, message: "请输入分配数量" },
                                {
                                    validator: (_, value) => {
                                        if (value == null) return Promise.resolve();
                                        if (value > availableQuantity) {
                                            return Promise.reject(
                                                new Error(`不能超过可用数量 ${availableQuantity}`),
                                            );
                                        }
                                        return Promise.resolve();
                                    },
                                },
                            ]}
                        >
                            <InputNumber
                                style={{ width: "100%" }}
                                min={1}
                                max={availableQuantity || undefined}
                                disabled={!canAllocate}
                                placeholder={`最多 ${availableQuantity}`}
                            />
                        </Form.Item>
                    </div>
                )}
                {!byIds && (
                    <div ref={startRef}>
                        <Form.Item
                            name="start_serial"
                            label="起始号（选填）"
                            extra={
                                rangeStart != null && rangeEnd != null
                                    ? `留空从最小可用号开始；号段范围 ${stock.start_serial} ~ ${stock.end_serial}`
                                    : "留空从最小可用号开始"
                            }
                            rules={[
                                {
                                    validator: (_, value) => {
                                        if (value == null || value === "") return Promise.resolve();
                                        if (rangeStart != null && value < rangeStart) {
                                            return Promise.reject(new Error(`起始号不能小于 ${rangeStart}`));
                                        }
                                        if (rangeEnd != null && value > rangeEnd) {
                                            return Promise.reject(new Error(`起始号不能大于 ${rangeEnd}`));
                                        }
                                        return Promise.resolve();
                                    },
                                },
                            ]}
                        >
                            <InputNumber
                                style={{ width: "100%" }}
                                min={rangeStart}
                                max={rangeEnd}
                                disabled={!canAllocate}
                                controls={false}
                                placeholder={rangeStart != null ? `如 ${rangeStart}` : "从最小可用号开始"}
                            />
                        </Form.Item>
                    </div>
                )}
                <div ref={dateRef}>
                    <Form.Item
                        name="allocation_date"
                        label="分配日期"
                        rules={[{ required: true, message: "请选择分配日期" }]}
                    >
                        <DatePicker style={{ width: "100%" }} />
                    </Form.Item>
                </div>
            </Form>

            <Tour
                open={guideOpen}
                onClose={() => setGuideOpen(false)}
                steps={guideSteps}
                zIndex={1100}
            />
        </Modal>
    );
};

export default MawbAllocationModal;
